import { createEffect, DEV, untrack } from "solid-js"
import { AnimationClip, AnimationMixer, Bone, Group, type AnimationAction, type Object3D } from "three"
import type { Engine } from "../../core/engine/Engine.type"
import type { EntityGraphNode } from "../../core/engine/EntityGraphNode/EntityGraphNode.type"
import applyEntityRotationToNextObject3DFrame from "../../core/entity/EntityHeading/applyEntityRotationToNextObject3DFrame"
import applyEntityWalkPathToNextObject3DFrame from "../../core/entity/EntityHeading/applyEntityWalkPathToNextObject3DFrame"
import type { InfantryEntity } from "../../core/entity/EntityType/Infantry"
import shouldEntityShowFadedFrames from "../../core/entity/shouldEntityShowFadedFrames"
import tmpRng from "../../core/rng/tmpRng"
import { HALF_PI } from "../../core/util/math/Math"
import { mountOnFrameUpdateToObject3D } from "../../event/GameObject3D/GameObject3DFrameUpdate/GameObject3DFrameUpdate"
import assertClassInDev from "../../ldsh/assertClassInDev"
import { HPBarAnchorName } from "../EntityThreeModel/addEntityThreeModelUnitSprite"
import { Quaternius_AssaultRifle2_1_AssaultRifle2_1_GLTF_Model, Quaternius_BlueSoldier_Male1_BlueSoldier_Male1_GLTF_Model } from "../gltf_models.generated"
import applyPlayerColors from "./applyPlayerColors"
import creaseMeshGeometryNormals from "./creaseMeshGeometryNormals"
import getBoneByName from "./getBoneByName"
import GLTFSignalPromiseFactory from './GLTFSignalPromiseFactory'
import isMesh from "./isMesh"
import runWithObject3DOwner from "./runWithObject3DOwner"
const totalLoopMs = 4000

const scale = 4

// const heights = [1, 2, 2, 3, 3, 4]

function getAnimationAction(mixer: AnimationMixer, animations: Array<AnimationClip>, name: string): AnimationAction {
  const clip: AnimationClip = animations.find(a => a.name === name) as AnimationClip
  if (DEV && !clip) {
    throw new Error('clip is null')
  }
  return mixer.clipAction(clip)
}

export default function makeInfantryScene(
  engine: Engine,
  ent: EntityGraphNode<InfantryEntity>,
  callback: (newGroup: Object3D) => void
): void {
  GLTFSignalPromiseFactory(Quaternius_BlueSoldier_Male1_BlueSoldier_Male1_GLTF_Model).then((ModelObj) => {
    tmpRng.seed = ent.id
    const { scene, animations } = ModelObj
    // console.log('Infantry.scene', scene)
    // console.log('animations', animations.map(a => a.name))

    const head = scene.getObjectByName('Head')
    assertClassInDev(head, Bone)
    const HPBarAnchor = new Group()
    HPBarAnchor.name = HPBarAnchorName
    head.add(HPBarAnchor)
    HPBarAnchor.position.y += 1.5
    HPBarAnchor.scale.divideScalar(scale * 0.9)

    const mixer = new AnimationMixer(scene)

    // const DeathAction = getAnimationAction(mixer, animations, 'Death')
    // const DefeatAction = getAnimationAction(mixer, animations, 'Defeat')
    const IdleAction = getAnimationAction(mixer, animations, 'Idle')
    // const JumpAction = getAnimationAction(mixer, animations, 'Jump')
    // const PickUpAction = getAnimationAction(mixer, animations, 'PickUp')
    // const PunchAction = getAnimationAction(mixer, animations, 'Punch')
    // const RecieveHitAction = getAnimationAction(mixer, animations, 'RecieveHit')
    // const RollAction = getAnimationAction(mixer, animations, 'Roll')
    // const RunAction = getAnimationAction(mixer, animations, 'Run')
    // const Run_CarryAction = getAnimationAction(mixer, animations, 'Run_Carry')
    // const Shoot_OneHandedAction = getAnimationAction(mixer, animations, 'Shoot_OneHanded')
    // const SitDownAction = getAnimationAction(mixer, animations, 'SitDown')
    // const StandUpAction = getAnimationAction(mixer, animations, 'StandUp')
    // const SwordSlashAction = getAnimationAction(mixer, animations, 'SwordSlash')
    // const VictoryAction = getAnimationAction(mixer, animations, 'Victory')
    const WalkAction = getAnimationAction(mixer, animations, 'Walk')
    // const Walk_CarryAction = getAnimationAction(mixer, animations, 'Walk_Carry')

    scene.scale.multiplyScalar(scale)
    // console.log('Infantry', InfantryScene)

    let currentAction = IdleAction
    currentAction.play()

    scene.userData.e = ent
    scene.userData.heading = 0
    scene.userData.targetHeading = 0

    let entValue = untrack(ent.raw)
    runWithObject3DOwner(scene, () => {
      createEffect(() => {
        entValue = ent.raw()
      })
    })


    mountOnFrameUpdateToObject3D(engine, scene, (elapsedMs: number, frameAt: number) => {
      if (shouldEntityShowFadedFrames(entValue)) {
        if (currentAction !== IdleAction) {
          currentAction.fadeOut(0.2)
          IdleAction.reset().fadeIn(0.2).play()
          currentAction = IdleAction
        }
      } else {
        const shouldWalk = (frameAt % totalLoopMs > (totalLoopMs / 2))
        if (shouldWalk && currentAction !== WalkAction) {
          currentAction.fadeOut(0.2)
          WalkAction.reset().fadeIn(0.2).play()
          currentAction = WalkAction
        } else if (!shouldWalk && currentAction !== IdleAction) {
          currentAction.fadeOut(0.2)
          IdleAction.reset().fadeIn(0.2).play()
          currentAction = IdleAction
        }
      }
      mixer.update(elapsedMs / 900)
      applyEntityRotationToNextObject3DFrame(scene, elapsedMs)
      applyEntityWalkPathToNextObject3DFrame(scene, elapsedMs)
    })

    // scene.add(scene)
    applyPlayerColors(engine, scene, ent)

    GLTFSignalPromiseFactory(Quaternius_AssaultRifle2_1_AssaultRifle2_1_GLTF_Model).then((gltf) => {
      const AssaultRifleScene = gltf.scene
      scene.add(AssaultRifleScene)
      // console.log('AssaultRifleScene', AssaultRifleScene)

      // console.log('FistR', InfantryScene.getObjectByName('FistR'))
      // console.log('FistL', InfantryScene.getObjectByName('FistL'))
      // console.log('FistL', InfantryScene.get('FistL'))

      const FistL = getBoneByName(scene, 'FistL')
      assertClassInDev(FistL, Bone)
      const FistR = getBoneByName(scene, 'FistR')
      assertClassInDev(FistR, Bone)

      AssaultRifleScene.rotateX(HALF_PI)
      AssaultRifleScene.rotateY(-HALF_PI)
      AssaultRifleScene.scale.multiplyScalar(1 / 2)

      // console.log(getBoneNameSet(AssaultRifleScene))
      // console.log((AssaultRifleScene))

      // "Bone",
      // "FootL",
      // "Body_1",
      // "Hips",
      // "Abdomen",
      // "Torso",
      // "Neck",
      // "Head",
      // "ShoulderL",
      // "UpperArmL",
      // "LowerArmL",
      // "FistL",
      // "ShoulderR",
      // "UpperArmR",
      // "LowerArmR",
      // "FistR",
      // "UpperLegL",
      // "LowerLegL",
      // "UpperLegR",
      // "LowerLegR",
      // "PoleTargetL",
      // "FootR",
      // "PoleTargetR",

      // const gripPoint = new Object3D()
      // gripPoint.position.set(2, 0, 2)
      // gripPoint.rotation.set(0, 0, 0)
      // AssaultRifleScene.add(gripPoint)

      FistR!.add(AssaultRifleScene)

      // const gripWorldPosition = new Vector3()
      // const gripWorldQuaternion = new Quaternion()

      // // Get grip point's world position and rotation
      // gripPoint.getWorldPosition(gripWorldPosition)
      // gripPoint.getWorldQuaternion(gripWorldQuaternion)

      // // Set left hand bone to the grip point's position
      // FistL!.position.copy(gripWorldPosition)
      // FistL!.quaternion.copy(gripWorldQuaternion)

      scene.traverse((mesh) => {
        if (isMesh(mesh)) {
          creaseMeshGeometryNormals(mesh)
        }
      })

      callback(scene)
    })
  })
}

